import { useState } from "react";
import { Plus, Trash2, Circle, CheckCircle2, ChevronDown, ChevronRight } from "lucide-react";
import { Card, Modal, Field, inputCls } from "./ui";
import { PRIORIDADES, EMPLEADOS, fmtDate, relDate, day, uid } from "./store";

const PRIO_CHIP = {
  "Alta": "bg-red-50 text-red-600 border-red-200",
  "Media": "bg-amber-50 text-amber-600 border-amber-200",
  "Baja": "bg-emerald-50 text-emerald-600 border-emerald-200",
};

export const Tasks = ({ data, upsertTask, removeTask }) => {
  const { tasks, leads } = data;
  const [filter, setFilter] = useState("Todas");
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({ titulo: "", leadId: "", fechaLimite: day(1), prioridad: "Media", descripcion: "", asignadoA: "" });
  
  // Inicializar todos los desplegables de empleados abiertos
  const [expanded, setExpanded] = useState(() => {
    const initial = { "Sin asignar": true };
    EMPLEADOS.forEach((emp) => (initial[emp] = true));
    return initial;
  });

  const leadName = (id) => leads.find((l) => l.id === id)?.nombre || "—";
  const overdue = (t) => t.estado !== "Completada" && t.fechaLimite < day(0);

  const counts = {
    Hoy: tasks.filter((t) => t.estado !== "Completada" && t.fechaLimite === day(0)).length,
    Semana: tasks.filter((t) => t.estado !== "Completada" && t.fechaLimite <= day(7)).length,
    Vencidas: tasks.filter(overdue).length,
    Completadas: tasks.filter((t) => t.estado === "Completada").length,
  };

  const filtered = tasks.filter((t) => {
    if (filter === "Todas") return true;
    if (filter === "Vencidas") return overdue(t);
    if (filter === "Completadas") return t.estado === "Completada";
    if (filter === "Pendientes") return t.estado !== "Completada";
    return t.prioridad === filter;
  });

  // Agrupar por empleado
  const grouped = {};
  const groups = [...EMPLEADOS, "Sin asignar"];
  groups.forEach((g) => (grouped[g] = []));
  
  filtered.forEach((t) => {
    const asignado = t.asignadoA || "Sin asignar";
    if (!grouped[asignado]) grouped[asignado] = [];
    grouped[asignado].push(t);
  });

  // Ordenar dentro de cada grupo
  Object.keys(grouped).forEach((g) => {
    grouped[g].sort((a, b) => (a.estado === "Completada") - (b.estado === "Completada") || a.fechaLimite.localeCompare(b.fechaLimite));
  });

  const toggleGroup = (g) => setExpanded((prev) => ({ ...prev, [g]: !prev[g] }));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        {[["Hoy", counts.Hoy], ["Esta semana", counts.Semana], ["Vencidas", counts.Vencidas], ["Completadas", counts.Completadas]].map(([k, n]) => (
          <button
            key={k}
            data-testid={`task-chip-${k}`}
            onClick={() => setFilter(k === "Esta semana" ? "Pendientes" : k)}
            className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${(filter === k || (k === "Esta semana" && filter === "Pendientes")) ? "border-[#2d5a3d] bg-[#2d5a3d] text-white" : "border-gray-200 bg-white text-gray-600 hover:border-[#2d5a3d]/40"}`}
          >
            {k} <span className="ml-1 opacity-70">{n}</span>
          </button>
        ))}
        {PRIORIDADES.map((p) => (
          <button key={p} data-testid={`task-filter-${p}`} onClick={() => setFilter(p)} className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${filter === p ? "border-[#2d5a3d] bg-[#2d5a3d] text-white" : "border-gray-200 bg-white text-gray-600 hover:border-[#2d5a3d]/40"}`}>
            {p}
          </button>
        ))}
        <button data-testid="task-new-btn" onClick={() => setShowForm(true)} className="ml-auto inline-flex items-center gap-2 rounded-lg bg-[#2d5a3d] px-4 py-2 text-sm font-semibold text-white hover:bg-[#3a7a50]">
          <Plus className="h-4 w-4" /> Nueva tarea
        </button>
      </div>

      <div className="space-y-6">
        {filtered.length === 0 && <Card className="p-8 text-center text-sm text-gray-400">No hay tareas con este filtro</Card>}
        
        {groups.map((groupName) => {
          const groupTasks = grouped[groupName] || [];
          if (filtered.length > 0 && groupTasks.length === 0 && filter !== "Todas") return null; // Ocultar vacíos si hay filtro activo
          
          const isExpanded = expanded[groupName];
          const pendientes = groupTasks.filter((t) => t.estado !== "Completada").length;

          return (
            <div key={groupName} className="space-y-3">
              <button 
                onClick={() => toggleGroup(groupName)}
                className="flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-5 py-3 transition-colors hover:bg-gray-50"
                data-testid={`task-group-${groupName.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? <ChevronDown className="h-5 w-5 text-gray-400" /> : <ChevronRight className="h-5 w-5 text-gray-400" />}
                  <span className="font-semibold text-gray-900">{groupName}</span>
                </div>
                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                  {pendientes} pendientes
                </span>
              </button>

              {isExpanded && (
                <div className="space-y-3 pl-4 sm:pl-8">
                  {groupTasks.length === 0 && <p className="text-xs text-gray-400 py-2">No hay tareas asignadas.</p>}
                  {groupTasks.map((t) => (
                    <Card key={t.id} data-testid={`task-row-${t.id}`} className={`flex items-center gap-4 px-5 py-4 ${overdue(t) ? "border-red-200 bg-red-50/40" : ""}`}>
                      <button data-testid={`task-check-${t.id}`} onClick={() => upsertTask({ ...t, estado: t.estado === "Completada" ? "Pendiente" : "Completada" })} className="shrink-0 text-gray-300 transition-colors hover:text-[#2d5a3d]" aria-label="Completar tarea">
                        {t.estado === "Completada" ? <CheckCircle2 className="h-5 w-5 text-[#2d5a3d]" /> : <Circle className="h-5 w-5" />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-medium ${t.estado === "Completada" ? "text-gray-400 line-through" : "text-gray-900"}`}>{t.titulo}</p>
                        <p className={`mt-0.5 text-xs ${overdue(t) ? "font-medium text-red-500" : "text-gray-400"}`}>
                          {leadName(t.leadId)} · {fmtDate(t.fechaLimite)}{relDate(t.fechaLimite) && t.estado !== "Completada" ? ` (${relDate(t.fechaLimite)})` : ""}
                        </p>
                      </div>
                      <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${PRIO_CHIP[t.prioridad] || ""}`}>{t.prioridad}</span>
                      <button data-testid={`task-delete-${t.id}`} onClick={() => removeTask(t.id)} className="shrink-0 rounded-lg p-1.5 text-gray-300 transition-colors hover:bg-red-50 hover:text-red-500" aria-label="Eliminar tarea">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showForm && (
        <Modal title="Nueva tarea" onClose={() => setShowForm(false)}>
          <form onSubmit={(e) => {
            e.preventDefault();
            if (!f.titulo.trim()) return;
            upsertTask({ ...f, id: uid(), estado: "Pendiente", leadId: f.leadId || null, asignadoA: f.asignadoA || null });
            setShowForm(false);
            setF({ titulo: "", leadId: "", fechaLimite: day(1), prioridad: "Media", descripcion: "", asignadoA: "" });
          }} className="space-y-4">
            <Field label="Título *">
              <input data-testid="task-field-titulo" className={inputCls} value={f.titulo} onChange={(e) => setF({ ...f, titulo: e.target.value })} placeholder="Enviar contrato de reserva..." />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Asignado a">
                <select data-testid="task-field-asignado" className={inputCls} value={f.asignadoA} onChange={(e) => setF({ ...f, asignadoA: e.target.value })}>
                  <option value="">— Nadie en concreto —</option>
                  {EMPLEADOS.map((emp) => <option key={emp} value={emp}>{emp}</option>)}
                </select>
              </Field>
              <Field label="Lead asociado">
                <select data-testid="task-field-lead" className={inputCls} value={f.leadId} onChange={(e) => setF({ ...f, leadId: e.target.value })}>
                  <option value="">— Ninguno —</option>
                  {leads.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
                </select>
              </Field>
              <Field label="Fecha límite">
                <input data-testid="task-field-fecha" type="date" className={inputCls} value={f.fechaLimite} onChange={(e) => setF({ ...f, fechaLimite: e.target.value })} />
              </Field>
              <Field label="Prioridad">
                <select data-testid="task-field-prioridad" className={inputCls} value={f.prioridad} onChange={(e) => setF({ ...f, prioridad: e.target.value })}>
                  {PRIORIDADES.map((p) => <option key={p}>{p}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Descripción">
              <textarea data-testid="task-field-descripcion" rows={3} className={inputCls} value={f.descripcion} onChange={(e) => setF({ ...f, descripcion: e.target.value })} />
            </Field>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">Cancelar</button>
              <button data-testid="task-form-submit" type="submit" className="rounded-lg bg-[#2d5a3d] px-5 py-2 text-sm font-semibold text-white hover:bg-[#3a7a50]">Crear tarea</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};
